import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PolicyIcon from '@mui/icons-material/Policy'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import HistoryIcon from '@mui/icons-material/History'
import { getRd, type ResearchDomain } from '../services/authApi'
import { MembersTab } from './tabs/MembersTab'
import { RolesTab } from './tabs/RolesTab'
import { GroupsTab } from './tabs/GroupsTab'
import { SettingsTab } from './tabs/SettingsTab'
import { BulkImportTab } from './tabs/BulkImportTab'

export function RdDetailPage() {
  const { rdId } = useParams<{ rdId: string }>()
  const navigate = useNavigate()
  const [rd, setRd] = useState<ResearchDomain | null>(null)
  const [tab, setTab] = useState(0)
  const [error, setError] = useState('')

  const loadRd = useCallback(async () => {
    if (!rdId) return
    try {
      const data = await getRd(rdId)
      setRd(data.rd)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load research domain')
    }
  }, [rdId])

  useEffect(() => { loadRd() }, [loadRd])

  if (!rdId) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate(rd ? `/orgs/${rd.orgId}` : '/orgs')} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3">{rd?.name ?? 'Loading...'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {rd?.slug} &middot; {rd?.memberCount ?? 0} member{rd?.memberCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<SmartToyIcon />} onClick={() => navigate(`/rds/${rdId}/agents`)}>Agents</Button>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => navigate(`/rds/${rdId}/audit`)}>Audit Log</Button>
          <Button variant="outlined" startIcon={<PolicyIcon />} onClick={() => navigate(`/rds/${rdId}/policies`)}>Policy Matrix</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label="Members" />
        <Tab label="Bulk Import" />
        <Tab label="Roles" />
        <Tab label="Groups" />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && <MembersTab rdId={rdId} />}
      {tab === 1 && <BulkImportTab rdId={rdId} />}
      {tab === 2 && <RolesTab rdId={rdId} />}
      {tab === 3 && <GroupsTab rdId={rdId} />}
      {tab === 4 && <SettingsTab rdId={rdId} />}
    </Box>
  )
}
